/**
 * Capture a full-height screenshot of the chat scroll container, including
 * everything currently scrolled off-screen.
 *
 * Strategy: let html2canvas do its own cloning (layout-faithful), then patch
 * the cloned target inside its `onclone` callback — expand its height so the
 * off-screen messages are in the layout, and copy each live <canvas>'s pixel
 * buffer into the corresponding cloned canvas. cloneNode() copies <canvas>
 * elements but not their rendered pixels, so chart.js charts come through
 * blank without that step.
 *
 * Usage:
 *   const canvas = await captureChatScroll(chatScrollRef.current, {
 *     title: 'My chat', model: 'claude-sonnet-4-6',
 *   });
 *   downloadCanvas(canvas, 'chat.png');
 */

import html2canvas from 'html2canvas';

// Collect every @font-face rule from accessible stylesheets in the live
// document and rewrite their relative url() references to absolute, anchored
// at the rule's owning stylesheet. The result is dropped into a <style> tag
// inside html2canvas's cloned document so its iframe sandbox can actually
// render with the TD brand fonts instead of falling back to system sans.
//
// Cached on `window` after first use — fonts don't change at runtime.
function collectFontFaceCss() {
  if (window.__chatScreenshotFontCss !== undefined) return window.__chatScreenshotFontCss;

  const out = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules;
    try { rules = sheet.cssRules; } catch (_) { continue; } // cross-origin stylesheet
    if (!rules) continue;
    const baseHref = sheet.href || document.baseURI;
    for (const rule of Array.from(rules)) {
      if (rule.type !== CSSRule.FONT_FACE_RULE) continue;
      // Resolve every url(...) in the rule to an absolute URL.
      const text = rule.cssText.replace(/url\((['"]?)([^'")]+)\1\)/g, (_m, q, ref) => {
        try {
          const abs = new URL(ref, baseHref).href;
          return `url("${abs}")`;
        } catch (_) {
          return `url(${q}${ref}${q})`;
        }
      });
      out.push(text);
    }
  }
  window.__chatScreenshotFontCss = out.join('\n');
  return window.__chatScreenshotFontCss;
}

function readBgColor(el) {
  let probe = el;
  while (probe) {
    const bg = getComputedStyle(probe).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') return bg;
    probe = probe.parentElement;
  }
  return '#ffffff';
}

// Climb from the scroll container to find the screen's actual header element.
// We look for .simple-header / .chat-header-outer up to a few levels up so we
// can sample its real styling (gradient, color, font) into the screenshot's
// top strip.
function findScreenHeader(scrollEl) {
  let probe = scrollEl.parentElement;
  for (let i = 0; i < 6 && probe; i++) {
    const hit = probe.querySelector('.simple-header, .chat-header-outer, .bh-flow-hero');
    if (hit) return hit;
    probe = probe.parentElement;
  }
  return null;
}

// The chat composer (suggestion pills + textbox + mic/send) is rendered as a
// sibling of the scroll container. Find it so we can include it in the final
// screenshot underneath the conversation.
function findInputArea(scrollEl) {
  let probe = scrollEl.parentElement;
  for (let i = 0; i < 4 && probe; i++) {
    const hit = probe.querySelector('.chat-input-area');
    if (hit) return hit;
    probe = probe.parentElement;
  }
  return null;
}


function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Build a header-strip canvas styled to match whatever the live screen's
// header looks like (gradient or solid, font, text color). We do this by
// constructing a synthetic DOM node, inheriting the live header's computed
// `background` and `color`, then capturing it with html2canvas. This gets us
// gradient fidelity and brand-font rendering for free.
async function buildHeaderStrip(scrollEl, { title, model, width, heightPx }) {
  const liveHeader = findScreenHeader(scrollEl);
  const cs = liveHeader ? getComputedStyle(liveHeader) : null;

  // Prefer the gradient (backgroundImage) if present; fall back to solid.
  let bgShorthand;
  if (cs && cs.backgroundImage && cs.backgroundImage !== 'none') {
    bgShorthand = cs.backgroundImage;
  } else if (cs && cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)') {
    bgShorthand = cs.backgroundColor;
  } else {
    bgShorthand = '#000000';
  }
  const textColor = (cs && cs.color) || '#ffffff';
  const fontFamily = (cs && cs.fontFamily) || `'TD Graphik', 'Inter', system-ui, sans-serif`;

  const div = document.createElement('div');
  // Off-screen but on-document so styles resolve.
  div.style.cssText = [
    'position: fixed',
    'left: -99999px',
    'top: 0',
    `width: ${width}px`,
    `height: ${heightPx}px`,
    'box-sizing: border-box',
    'padding: 12px 16px',
    `background: ${bgShorthand}`,
    `color: ${textColor}`,
    'display: flex',
    'flex-direction: column',
    'justify-content: center',
    `font-family: ${fontFamily}`,
    'overflow: hidden',
  ].join(';');
  div.innerHTML = `
    <div style="font-weight: 700; font-size: 18px; line-height: 1.2;">${escapeHtml(title || 'Chat')}</div>
    ${model ? `<div style="font-size: 12px; opacity: 0.85; margin-top: 2px;">${escapeHtml(model)}</div>` : ''}
  `;
  document.body.appendChild(div);

  try {
    const canvas = await html2canvas(div, {
      backgroundColor: null,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      logging: false,
      width,
      height: heightPx,
      windowWidth: width,
      windowHeight: heightPx,
      useCORS: true,
      onclone: (clonedDoc) => {
        try {
          const fontCss = collectFontFaceCss();
          if (fontCss) {
            const style = clonedDoc.createElement('style');
            style.setAttribute('data-chat-screenshot', 'font-faces');
            style.textContent = fontCss;
            (clonedDoc.head || clonedDoc.body).appendChild(style);
          }
        } catch (_) { /* ignore */ }
      },
    });
    return canvas;
  } finally {
    document.body.removeChild(div);
  }
}

// onclone is synchronous. cloneNode() copies <canvas> ELEMENTS but not their
// pixel buffers — so chart.js / react-chartjs-2 charts come through blank.
// Match up live canvases with their clones by document order and drawImage()
// the pixels across. Backing-store dimensions are copied; CSS sizing carries
// over from cloneNode automatically.
function copyCanvasPixels(liveRoot, cloneRoot) {
  const liveCanvases  = liveRoot.querySelectorAll('canvas');
  const cloneCanvases = cloneRoot.querySelectorAll('canvas');
  const n = Math.min(liveCanvases.length, cloneCanvases.length);
  for (let i = 0; i < n; i++) {
    const src = liveCanvases[i];
    const dst = cloneCanvases[i];
    if (!src.width || !src.height) continue;
    try {
      // Note: writing to canvas.width/height clears the canvas, so we set
      // dimensions BEFORE drawImage.
      dst.width  = src.width;
      dst.height = src.height;
      dst.getContext('2d').drawImage(src, 0, 0);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[chatScreenshot] canvas copy failed at index', i, err);
    }
  }
}

// Walk up from the scroll element to its .chat-screen ancestor (the flex
// column wrapping header + scroll-content + chat-input-area).
function findChatScreen(scrollEl) {
  let probe = scrollEl;
  while (probe) {
    if (probe.classList && probe.classList.contains('chat-screen')) return probe;
    probe = probe.parentElement;
  }
  return scrollEl.parentElement; // fallback
}

/**
 * Captures the full chat screen — header strip + the whole conversation
 * (including content scrolled off-screen) + the composer / input area —
 * as a single PNG canvas.
 *
 * @param {HTMLElement} scrollEl - the scroll container (e.g. chatScrollRef.current)
 * @param {object} [opts]
 * @param {string} [opts.title]  - Optional title shown in the top header strip
 * @param {string} [opts.model]  - Optional model name shown under the title
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function captureChatScroll(scrollEl, opts = {}) {
  if (!scrollEl) throw new Error('captureChatScroll: scrollEl is required');

  // Wait for any in-flight font loads so text renders with the right metrics.
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (_) { /* non-fatal */ }
  }

  const chatScreen = findChatScreen(scrollEl);
  const bg = readBgColor(scrollEl);

  // The capture target is the whole .chat-screen. We hide the live header in
  // onclone (since we replace it with a clean title/model strip) and remove
  // the scroll-content's height/overflow constraints so the conversation lays
  // out at its full height. html2canvas's own layout engine then sizes the
  // captured canvas to: (live-header-hidden) + (expanded scroll-content) +
  // (chat-input-area). No manual height math, no trailing whitespace.
  const screenRect = chatScreen.getBoundingClientRect();
  const fullW = chatScreen.scrollWidth || screenRect.width;
  // We'll let html2canvas measure the real height of the cloned element after
  // onclone runs — passing scrollHeight here is a generous upper bound; the
  // browser still respects the expanded layout. Use sum of header + scroll
  // content + input area heights as the explicit target for windowHeight.
  const inputAreaEl = findInputArea(scrollEl);
  const inputAreaH  = inputAreaEl ? inputAreaEl.getBoundingClientRect().height : 0;
  // Generous upper bound — the actual rendered height is measured inside
  // onclone (after our DOM mutations) and used to crop the output below.
  const fullH = scrollEl.scrollHeight + inputAreaH + 200;

  const scale = Math.min(window.devicePixelRatio || 1, 2);
  let measuredHeight = fullH; // gets overwritten in onclone

  const bodyCanvas = await html2canvas(chatScreen, {
    backgroundColor: bg,
    scale,
    useCORS: true,
    logging: false,
    width: fullW,
    height: fullH,
    windowWidth: fullW,
    windowHeight: fullH,
    onclone: (clonedDoc, clonedScreen) => {
      // Inject @font-face rules with absolute URLs so the cloned iframe can
      // render the TD brand fonts. Without this html2canvas falls back to
      // system sans-serif.
      try {
        const fontCss = collectFontFaceCss();
        if (fontCss) {
          const style = clonedDoc.createElement('style');
          style.setAttribute('data-chat-screenshot', 'font-faces');
          style.textContent = fontCss;
          (clonedDoc.head || clonedDoc.body).appendChild(style);
        }
      } catch (_) { /* ignore */ }

      // Hide the cloned live header — we paint our own clean strip on top.
      try {
        const clonedHeader = clonedScreen.querySelector('.simple-header, .chat-header-outer');
        if (clonedHeader) clonedHeader.style.display = 'none';
      } catch (_) { /* ignore */ }

      // Drop the bottom-nav padding (62px on .screen) — there's no bottom nav
      // in the saved PNG, so it's just dead whitespace. Don't touch the
      // flex/height of .chat-screen itself: changing those breaks the child
      // layout cascade (user-bubble alignment, text wrapping) because the
      // bubbles' CSS depends on the chat-screen being a normal flex column.
      try {
        clonedScreen.style.paddingBottom = '0';
      } catch (_) { /* ignore */ }

      // Expand the cloned scroll-content so all messages are in the layout.
      try {
        const clonedScroll = clonedScreen.querySelector('.scroll-content');
        if (clonedScroll) {
          Object.assign(clonedScroll.style, {
            flex: 'none',
            height: 'auto',
            maxHeight: 'none',
            minHeight: 'auto',
            overflow: 'visible',
            overflowY: 'visible',
            overflowX: 'visible',
          });
        }
      } catch (_) { /* ignore */ }

      // Copy live <canvas> pixel buffers into the cloned canvases (chart.js).
      copyCanvasPixels(chatScreen, clonedScreen);

      // Force layout, then measure the bottom of the cloned input area
      // relative to the chat-screen top. That's the true end-of-content; using
      // clonedScreen.scrollHeight gives an inflated value because .chat-screen
      // still has flex: 1 and stretches to fill the html2canvas iframe.
      // eslint-disable-next-line no-unused-expressions
      clonedScreen.offsetHeight;
      const clonedInput = clonedScreen.querySelector('.chat-input-area');
      if (clonedInput) {
        const inputRect  = clonedInput.getBoundingClientRect();
        const screenRect = clonedScreen.getBoundingClientRect();
        measuredHeight = inputRect.bottom - screenRect.top;
      } else {
        // Fallback if there's no composer (e.g. modal chat) — measure last
        // child of the expanded scroll content.
        const clonedScroll = clonedScreen.querySelector('.scroll-content');
        const last = clonedScroll && clonedScroll.lastElementChild;
        if (last) {
          const lastRect   = last.getBoundingClientRect();
          const screenRect = clonedScreen.getBoundingClientRect();
          measuredHeight = lastRect.bottom - screenRect.top;
        }
      }
    },
  });

  // Trim the body canvas to the measured height (the html2canvas output was
  // sized to a generous upper bound).
  const targetPx = Math.min(bodyCanvas.height, Math.ceil(measuredHeight * scale));
  let trimmedBody = bodyCanvas;
  if (targetPx > 0 && targetPx < bodyCanvas.height) {
    trimmedBody = document.createElement('canvas');
    trimmedBody.width = bodyCanvas.width;
    trimmedBody.height = targetPx;
    trimmedBody.getContext('2d').drawImage(
      bodyCanvas, 0, 0, bodyCanvas.width, targetPx, 0, 0, bodyCanvas.width, targetPx
    );
  }

  // Header strip (matches the live header's gradient/color/font) — drawn on
  // top of the body capture rather than inside it, so we don't show back/save
  // controls in the saved PNG.
  if (!opts.title && !opts.model) return trimmedBody;

  const headerCssH = 64;
  const headerCanvas = await buildHeaderStrip(scrollEl, {
    title: opts.title,
    model: opts.model,
    width: fullW,
    heightPx: headerCssH,
  });

  const out = document.createElement('canvas');
  out.width  = trimmedBody.width;
  out.height = headerCanvas.height + trimmedBody.height;
  const ctx = out.getContext('2d');
  ctx.drawImage(headerCanvas, 0, 0, out.width, headerCanvas.height);
  ctx.drawImage(trimmedBody, 0, headerCanvas.height);
  return out;
}

export function downloadCanvas(canvas, filename) {
  const url = canvas.toDataURL('image/png');
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  return url;
}

// Back-compat shim — old callers still importing this get a clear warning.
export function generateChatScreenshot({ title, model } = {}) {
  // eslint-disable-next-line no-console
  console.warn(
    '[chatScreenshot] generateChatScreenshot() is deprecated. Use captureChatScroll(scrollEl, opts).'
  );
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 160;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#1a1a2e';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 18px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(title || 'Conversation', canvas.width / 2, 50);
  ctx.font = '14px sans-serif';
  ctx.fillStyle = '#9ca3af';
  ctx.fillText(model || '', canvas.width / 2, 80);
  ctx.fillStyle = '#ef4444';
  ctx.fillText('(deprecated path — switch caller to captureChatScroll)', canvas.width / 2, 130);
  return canvas;
}
